import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabaseUrl, supabaseKey } from '../config.ts';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(supabaseUrl, supabaseKey);

function ServiceDetails() {
    const { id } = useParams();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    // New state variables for feedback popup
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackRating, setFeedbackRating] = useState(5); // Default rating
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');

    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        length: '',
        width: '',
        unit: 'Feet', // New state field for unit, defaulting to 'Feet'
        service: '',
        message: '',
        city: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSuccess(false);

        let supabaseError = null;
        try {
            const { error } = await supabase.from('inquiries').insert([
                {
                    first_name: form.first_name,
                    last_name: form.last_name,
                    email: form.email,
                    phone: form.phone,
                    length: form.length,
                    width: form.width,
                    unit: form.unit, // Include unit in submission
                    service: form.service,
                    message: form.message,
                    status: 'new',
                    city: form.city,
                }
            ]);
            supabaseError = error;
        } catch (err) {
            supabaseError = err;
        }

        if (!supabaseError) {
            // Trigger N8N webhook after successful Supabase submission
            try {
                const response = await fetch('https://n8n.nicknameinfo.net/webhook/98f0cb09-8c82-4599-8f7b-c3f442b0128f', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        firstName: form.first_name,
                        lastName: form.last_name,
                        phone: form.phone,
                        city: form.city,
                        email: form.email,
                        length: form.length,
                        width: form.width,
                        unitOfMeasurement: form.unit, // Use 'unitOfMeasurement' as per payload spec
                        service: form.service,
                        message: form.message,
                    }),
                });

                if (!response.ok) {
                    console.warn(`N8N webhook responded with status: ${response.status}. It might not have processed correctly.`);
                } else {
                    console.log('N8N webhook successfully triggered!');
                }
            } catch (apiError) {
                console.error('Error triggering N8N webhook:', apiError);
                // This error will not prevent the user from seeing the success message
                // as the primary inquiry submission to Supabase was successful.
            }
            setSuccess(true);
            setForm({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                length: '',
                width: '',
                unit: 'Feet', // Reset unit to default after submission
                service: '',
                message: '',
                city: '',
            });
        } else {
            console.error('Supabase submission error:', supabaseError);
            // You might want to add user-facing error handling here for Supabase failures
        }
        setSubmitting(false); // Always set to false at the end
    };

    const handleSubmitFeedback = async () => {
        setFeedbackError('');
        // If no text is provided AND the rating is still the default (5), prompt for input.
        // This encourages interaction but allows submitting just a rating or just text.
        if (!feedbackText.trim() && feedbackRating === 5) {
            setFeedbackError('Please provide some comments or adjust the rating.');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('feedback')
                .insert([
                    { comment: feedbackText, rating: feedbackRating }
                ]);

            if (error) {
                throw error;
            }

            setFeedbackSubmitted(true);
            // Optionally, close popup after a short delay or immediately
            setTimeout(() => {
                setShowFeedbackPopup(false);
                setFeedbackText('');
                setFeedbackRating(5);
                setFeedbackSubmitted(false);
            }, 1500); // Close after 1.5 seconds
        } catch (error) {
            console.error('Error submitting feedback:', error.message);
            setFeedbackError('Failed to submit feedback. Please try again.');
        }
    };

    const handleCloseFeedback = () => {
        setShowFeedbackPopup(false);
        setFeedbackText('');
        setFeedbackRating(5);
        setFeedbackSubmitted(false);
        setFeedbackError('');
    };

    useEffect(() => {
        const fetchService = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('services')
                .select('*')
                .eq('id', id)
                .single();
            if (!error) {
                setService(data);
                // Set the service in the form state when service data is fetched
                setForm(prev => ({ ...prev, service: data.title || '' }));
            }
            setLoading(false);
        };
        fetchService();
    }, [id]);

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!service) {
        return (
            <div className="container py-5 text-center">
                <h3>Service not found.</h3>
            </div>
        );
    }

    return (
        <>

            {/* Header Start */}
            <div className="container-fluid bg-breadcrumb">
                <ul className="breadcrumb-animation">
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                    <li />
                </ul>
                <div className="container text-center py-5" style={{ maxWidth: 900 }}>
                    <h3 className="display-3 mb-4 wow fadeInDown" data-wow-delay="0.1s">
                        Our Services
                    </h3>
                    <ol
                        className="breadcrumb justify-content-center mb-0 wow fadeInDown"
                        data-wow-delay="0.3s"
                    >
                        <li className="breadcrumb-item">
                            <a href="/">Home</a>
                        </li>
                        <li className="breadcrumb-item active text-primary">Service</li>
                    </ol>
                </div>
            </div>
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="row g-4 align-items-stretch">
                            {/* Service Details Left */}
                            <div className="col-md-6">
                                <div className="card shadow-lg border-0 rounded-4 h-100 overflow-hidden">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="img-fluid w-100 object-fit-cover"
                                        style={{ minHeight: 220, maxHeight: 320, objectFit: "cover" }}
                                    />
                                    <div className="card-body p-4">

                                        <h2 className="card-title mb-3 fw-bold" style={{ fontSize: "2rem" }}>
                                            {service.title}
                                        </h2>
                                        <p className="card-text text-secondary" style={{ fontSize: "1.1rem" }}>
                                            {service.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Inquiry Form Right */}
                            <div className="col-md-6">
                                <div className="card shadow rounded-4 h-100">
                                    <div className="card-body p-4 d-flex flex-column justify-content-center">
                                        <h4 className="mb-4 text-center">Service Inquiry Form</h4>
                                        {success && (
                                            <div className="alert alert-success text-center" role="alert">
                                                Thank you! Your inquiry has been submitted.
                                            </div>
                                        )}
                                        <form onSubmit={handleSubmit} autoComplete="off">
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="first_name"
                                                            id="first_name"
                                                            maxLength="255"
                                                            placeholder="Enter first name"
                                                            value={form.first_name}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <label htmlFor="first_name">First Name <span className="text-danger">*</span></label>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="last_name"
                                                            id="last_name"
                                                            maxLength="255"
                                                            placeholder="Enter last name"
                                                            value={form.last_name}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <label htmlFor="last_name">Last Name <span className="text-danger">*</span></label>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="tel"
                                                            className="form-control"
                                                            name="phone"
                                                            id="phone"
                                                            maxLength="15"
                                                            placeholder="Enter phone number"
                                                            value={form.phone}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <label htmlFor="phone">Phone <span className="text-danger">*</span></label>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="city"
                                                            id="city"
                                                            placeholder="Enter City"
                                                            value={form.city}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <label htmlFor="city">City <span className="text-danger">*</span></label>
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            name="email"
                                                            id="email"
                                                            maxLength="255"
                                                            placeholder="Enter email address"
                                                            value={form.email}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <label htmlFor="email">Email <span className="text-danger">*</span></label>
                                                    </div>
                                                </div>
                                                {/* New Length and Width Fields */}
                                                <div className="col-md-6">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="length"
                                                            id="length"
                                                            placeholder="Enter length"
                                                            value={form.length}
                                                            onChange={handleChange}
                                                        />
                                                        <label htmlFor="length">Length</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="width"
                                                            id="width"
                                                            placeholder="Enter width"
                                                            value={form.width}
                                                            onChange={handleChange}
                                                        />
                                                        <label htmlFor="width">Width</label>
                                                    </div>
                                                </div>
                                                {/* New Unit Field */}
                                                <div className="col-12">
                                                    <div className="form-floating mb-3">
                                                        <select
                                                            className="form-select"
                                                            name="unit"
                                                            id="unit"
                                                            value={form.unit}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="Inches">Inches</option>
                                                            <option value="Feet">Feet</option>
                                                            <option value="Yards">Yards</option>
                                                            <option value="Miles">Miles</option>
                                                            <option value="Meters">Meters</option>
                                                            <option value="Kilometers">Kilometers</option>
                                                            <option value="Centimeters">Centimeters</option>
                                                        </select>
                                                        <label htmlFor="unit">Target Unit of Measurement</label>
                                                    </div>
                                                </div>
                                                {/* End New Unit Field */}
                                                <div className="col-12">
                                                    <div className="form-floating mb-3">
                                                        <select
                                                            className="form-select"
                                                            name="service"
                                                            id="service"
                                                            value={form.service}
                                                            onChange={handleChange}
                                                            required
                                                        >
                                                            <option value="">Select Service</option>
                                                            <option value="Pre-Construction Services">Pre-Construction Services</option>
                                                            <option value="General Construction Services">General Construction Services</option>
                                                            <option value="Renovation and Remodeling">Renovation and Remodeling</option>
                                                            <option value="Civil Construction Services">Civil Construction Services</option>
                                                            <option value="Project Management">Project Management</option>
                                                            <option value="Sustainable and Green Construction">Sustainable and Green Construction</option>
                                                        </select>
                                                        <label htmlFor="service">Select Service <span className="text-danger">*</span></label>
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <div className="form-floating mb-3">
                                                        <textarea
                                                            className="form-control"
                                                            name="message"
                                                            id="message"
                                                            placeholder="Your message"
                                                            style={{ minHeight: 80 }}
                                                            value={form.message}
                                                            onChange={handleChange}
                                                        />
                                                        <label htmlFor="message">Message</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-center mt-4">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-lg px-5"
                                                    disabled={submitting}
                                                >
                                                    {submitting ? 'Submitting...' : 'Submit'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Feedback Popup Modal */}
            {showFeedbackPopup && (
                <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            )}
            {showFeedbackPopup && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content bg-dark text-white">
                            <div className="modal-header border-bottom-0">
                                <h5 className="modal-title">Provide Feedback</h5>
                                <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={handleCloseFeedback}></button>
                            </div>
                            <div className="modal-body">
                                {!feedbackSubmitted ? (
                                    <>
                                        {feedbackError && <div className="alert alert-danger">{feedbackError}</div>}
                                        <div className="mb-3">
                                            <label htmlFor="feedbackText" className="form-label">Comments (Optional):</label>
                                            <textarea
                                                className="form-control bg-light text-dark border rounded-3 shadow-sm" // Updated styling
                                                id="feedbackText"
                                                rows="3"
                                                value={feedbackText}
                                                onChange={(e) => setFeedbackText(e.target.value)}
                                                placeholder="Tell us what you think..."
                                            ></textarea>
                                        </div>
                                        <div className="mb-3 text-center"> {/* Centered for star rating */}
                                            <label className="form-label d-block mb-2">Rating:</label>
                                            <div className="star-rating">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <span
                                                        key={star}
                                                        className="star"
                                                        style={{
                                                            cursor: 'pointer',
                                                            fontSize: '2rem', // Larger stars
                                                            color: star <= feedbackRating ? '#ffc107' : '#e4e5e9', // Gold for filled, light gray for empty
                                                            transition: 'color 0.2s ease-in-out', // Smooth transition
                                                        }}
                                                        onClick={() => setFeedbackRating(star)}
                                                    >
                                                        &#9733; {/* Unicode star character */}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center text-success">
                                        <p className="mb-0">Thank you for your feedback!</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-top-0">
                                {!feedbackSubmitted && (
                                    <>
                                        <button type="button" className="btn btn-secondary" onClick={handleCloseFeedback}>
                                            Cancel
                                        </button>
                                        <button type="button" className="btn btn-primary" onClick={handleSubmitFeedback}>
                                            Submit Feedback
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default ServiceDetails;